import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ArtifactData, ArtifactState, ArtifactVersion } from '../ai-chat/aiChatTypes';
import { selectActiveChatId, TEMP_CHAT_ID } from '../../aiAgentCenter/agentsChatsReducer';
import { useAgentChatInstanceOptional } from '../../aiAgentCenter/AgentChatInstanceContext';
import { ChatInstance } from '../../aiAgentCenter/ChatInstance';
import { useIsCanvasUnited } from '../../../hooks/useIsCanvasUnited';
import useAgentsHome from '../../aiAgentCenter/useAgentsHome';

interface ArtifactContextType {
  // Panel state
  isArtifactPanelOpen: boolean;
  currentArtifactId: string | null;
  artifactState: ArtifactState;
  streamingContent: string;
  isStreamingComplete: boolean;

  // Data
  artifacts: Record<string, ArtifactData>;

  // Actions
  openArtifactPanel: (artifactId: string) => void;
  openExistingArtifact: (artifactId: string, versionId?: string) => void;
  closeArtifactPanel: () => void;
  setArtifactState: (state: ArtifactState) => void;
  addStreamingContent: (content: string) => void;
  restoreStreamingContent: (content: string) => void;
  addArtifactData: (artifactId: string, data: ArtifactData) => void;
  updateArtifactVersion: (
    artifactId: string,
    versionId: string,
    versionData: Partial<ArtifactVersion>,
  ) => void;
  updateArtifactSharingStatus: (artifactId: string, isShared: boolean, isPublic: boolean) => void;
  startArtifactStreaming: (artifactId: string) => void;
  setStreamingComplete: (complete: boolean) => void;
  completeArtifactStreaming: () => void;
}

const ArtifactContext = createContext<ArtifactContextType | null>(null);

export const ArtifactProvider: React.FC<{
  children: React.ReactNode;
  forceSidebarCollapse?: () => void;
}> = ({ children, forceSidebarCollapse }) => {
  const [isArtifactPanelOpen, setIsArtifactPanelOpen] = useState(false);
  const [currentArtifactId, setCurrentArtifactId] = useState<string | null>(null);
  const [artifactState, setArtifactState] = useState<ArtifactState>('streaming');
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreamingComplete, setIsStreamingComplete] = useState(false);
  const [artifacts, setArtifacts] = useState<Record<string, ArtifactData>>({});

  // Panel expansion integration - expand panel when artifact opens (canvas/funnel pages)
  const isCanvasUnited = useIsCanvasUnited();
  const { setIsExpanded } = useAgentsHome();

  // Get current active chat ID to detect changes (optional - may not be available in dialogs)
  const chatInstanceContext = useAgentChatInstanceOptional();
  const chatInstanceId = chatInstanceContext?.chatInstanceId ?? ChatInstance.agentsPage;
  const activeChatId = useSelector(selectActiveChatId(chatInstanceId));
  const previousActiveChatId = useRef<string | undefined>(activeChatId);

  const resetArtifactState = useCallback((clearArtifacts = false) => {
    setIsArtifactPanelOpen(false);
    setCurrentArtifactId(null);
    setStreamingContent('');
    setIsStreamingComplete(false);
    setArtifactState('streaming');
    if (clearArtifacts) {
      setArtifacts({});
    }
  }, []);

  // Close artifact panel when active chat changes or when in ghost/temp chat
  useEffect(() => {
    const chatIdChanged = previousActiveChatId.current !== activeChatId;
    const hadPreviousChat = previousActiveChatId.current !== undefined;
    const isGhostOrTempChat = activeChatId === TEMP_CHAT_ID || activeChatId === 'GHOST_CHAT_ID';
    const willReset =
      isArtifactPanelOpen && hadPreviousChat && (chatIdChanged || isGhostOrTempChat);

    // Close panel if: switching chats OR panel is open in ghost/temp chat (stale state)
    if (willReset) {
      resetArtifactState(true);
    }

    // Update the ref to track the current chat ID
    previousActiveChatId.current = activeChatId;
  }, [activeChatId, isArtifactPanelOpen, resetArtifactState, currentArtifactId]);

  // Close artifact panel when component unmounts (e.g., when navigating away from agent center)
  useEffect(() => {
    return () => {
      if (isArtifactPanelOpen) {
        // Clean up artifact panel state on unmount
        resetArtifactState(false);
      }
    };
  }, [isArtifactPanelOpen, resetArtifactState]);

  const openArtifactPanel = useCallback((artifactId: string) => {
    setCurrentArtifactId(artifactId);
    setIsArtifactPanelOpen(true);
    setArtifactState('streaming');
    setStreamingContent('');
  }, []);

  const openExistingArtifact = useCallback(
    (artifactId: string, versionId?: string) => {
      // Canvas/funnel-specific: expand agent panel to full screen when artifact opens
      if (isCanvasUnited) {
        setIsExpanded({ isExpanded: true });
      }

      setCurrentArtifactId(artifactId);
      setIsArtifactPanelOpen(true);
      setArtifactState('rendered');
      setStreamingContent('');

      // If a specific version was requested, update the artifact's currentVersion
      if (versionId) {
        setArtifacts((prev) => {
          const artifact = prev[artifactId];
          if (!artifact) return prev;

          return {
            ...prev,
            [artifactId]: {
              ...artifact,
              currentVersion: versionId,
            },
          };
        });
      }
    },
    [isCanvasUnited, setIsExpanded],
  );

  const closeArtifactPanel = useCallback(() => {
    resetArtifactState(false);
  }, [resetArtifactState]);

  const addStreamingContent = useCallback((content: string) => {
    setStreamingContent((prev) => prev + content);
  }, []);

  const restoreStreamingContent = useCallback((content: string) => {
    setStreamingContent(content);
  }, []);

  const addArtifactData = useCallback((artifactId: string, data: ArtifactData) => {
    setArtifacts((prev) => ({
      ...prev,
      [artifactId]: data,
    }));
  }, []);

  const updateArtifactVersion = useCallback(
    (artifactId: string, versionId: string, versionData: Partial<ArtifactVersion>) => {
      setArtifacts((prev) => {
        const artifact = prev[artifactId];
        if (!artifact) return prev;

        const updatedVersions = artifact.versions.map((v) =>
          v.versionId === versionId ? { ...v, ...versionData } : v,
        );

        return {
          ...prev,
          [artifactId]: {
            ...artifact,
            versions: updatedVersions,
          },
        };
      });
    },
    [],
  );

  const updateArtifactSharingStatus = useCallback(
    (artifactId: string, isShared: boolean, isPublic: boolean) => {
      setArtifacts((prev) => {
        const artifact = prev[artifactId];
        if (!artifact) return prev;

        return {
          ...prev,
          [artifactId]: {
            ...artifact,
            isShared,
            isPublic,
          },
        };
      });
    },
    [],
  );

  const startArtifactStreaming = useCallback(
    (artifactId: string) => {
      forceSidebarCollapse?.();

      // Canvas/funnel-specific: expand agent panel to full screen when artifact opens
      if (isCanvasUnited) {
        setIsExpanded({ isExpanded: true });
      }

      setCurrentArtifactId(artifactId);
      setArtifactState('streaming');
      setStreamingContent('');
      setIsStreamingComplete(false);
      setIsArtifactPanelOpen(true);
    },
    [forceSidebarCollapse, isCanvasUnited, setIsExpanded],
  );

  const setStreamingComplete = useCallback((complete: boolean) => {
    setIsStreamingComplete(complete);
  }, []);

  const completeArtifactStreaming = useCallback(() => {
    setArtifactState('compiling');

    // Keep the streaming indicator visible for a moment to show completion
    setTimeout(() => {
      setArtifactState('rendered');
    }, 1500);
  }, []);

  const value: ArtifactContextType = {
    isArtifactPanelOpen,
    currentArtifactId,
    artifactState,
    streamingContent,
    isStreamingComplete,
    artifacts,
    openArtifactPanel,
    openExistingArtifact,
    closeArtifactPanel,
    setArtifactState,
    addStreamingContent,
    restoreStreamingContent,
    addArtifactData,
    updateArtifactVersion,
    updateArtifactSharingStatus,
    startArtifactStreaming,
    setStreamingComplete,
    completeArtifactStreaming,
  };

  return <ArtifactContext.Provider value={value}>{children}</ArtifactContext.Provider>;
};

export const useArtifacts = (): ArtifactContextType => {
  const context = useContext(ArtifactContext);
  if (!context) {
    throw new Error('useArtifacts must be used within an ArtifactProvider');
  }
  return context;
};

/**
 * Optional version of useArtifacts that returns undefined if not within a provider.
 * Useful for components that may or may not have artifact support.
 */
export const useArtifactsOptional = (): ArtifactContextType | undefined => {
  const context = useContext(ArtifactContext);
  return context ?? undefined;
};

// Mock artifact streaming (for testing) - optimized for speed
export const simulateArtifactStreaming = (
  artifactId: string,
  artifactContent: string,
  onChunk: (chunk: string) => void,
  onStreamingComplete: () => void,
) => {
  const chunks = artifactContent.split('');
  let currentIndex = 0;

  const getChunkSize = () => {
    return Math.random() > 0.7 ? 15 : Math.random() > 0.4 ? 12 : 8;
  };

  const getInterval = () => {
    return 6;
  };

  const streamInterval = setInterval(() => {
    if (currentIndex < chunks.length) {
      const chunkSize = getChunkSize();
      const endIndex = Math.min(currentIndex + chunkSize, chunks.length);
      const charactersToStream = chunks.slice(currentIndex, endIndex).join('');

      onChunk(charactersToStream);
      currentIndex = endIndex;
    } else {
      clearInterval(streamInterval);
      onStreamingComplete();
    }
  }, getInterval());

  return () => clearInterval(streamInterval);
};
